/**
 * Single-source-of-truth hook for "who am I".
 *
 * Returns one of:
 *   - status='loading'   — initial fetch in flight
 *   - status='anonymous' — no session cookie
 *   - status='first-launch' — authenticated AS superadmin but no admin_user rows exist yet
 *   - status='pending'   — admin_user.status='pending_oidc_approval'
 *   - status='active'    — fully signed in; ``user`` is populated
 *   - status='error'     — fetch failed (operator should see an error banner)
 *
 * The hook fetches ``/api/auth/me`` and (only when authenticated) ``/api/setup``
 * once on mount. ``refresh()`` re-runs both. Consumers use the ``status``
 * field to branch in render code.
 */

import { useCallback, useEffect, useState } from "react";

import { ApiError, type MeResponse, api } from "../api/client";

export type CurrentUserStatus =
  | "loading"
  | "anonymous"
  | "first-launch"
  | "pending"
  | "active"
  | "error";

export interface CurrentUserState {
  status: CurrentUserStatus;
  user: MeResponse | null;
  error: string | null;
  refresh: () => Promise<void>;
}

export function useCurrentUser(): CurrentUserState {
  const [user, setUser] = useState<MeResponse | null>(null);
  const [status, setStatus] = useState<CurrentUserStatus>("loading");
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setStatus("loading");
    setError(null);
    try {
      const me = await api.me();
      setUser(me);
      if (me.username === null) {
        setStatus("anonymous");
        return;
      }
      if (me.status === "pending_oidc_approval") {
        setStatus("pending");
        return;
      }
      // Authenticated. Check first-launch only when superadmin so we can
      // bounce them to /setup; regular admins past first-launch never need
      // the extra call.
      if (me.is_superadmin) {
        try {
          const s = await api.setupStatus();
          if (s.first_launch) {
            setStatus("first-launch");
            return;
          }
        } catch {
          // setup endpoint is best-effort; if it fails we still let the
          // superadmin into the app.
        }
      }
      setStatus("active");
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        setUser(null);
        setStatus("anonymous");
        return;
      }
      setStatus("error");
      setError(err instanceof Error ? err.message : String(err));
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return { status, user, error, refresh };
}
